import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Plus, Trash2, Edit, Calendar } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeColors = {
  class: "bg-warm-50 text-warm-700",
  workshop: "bg-purple-50 text-purple-700",
  meeting: "bg-blue-50 text-blue-700",
  community: "bg-amber-50 text-amber-700",
  fundraiser: "bg-pink-50 text-pink-700",
  other: "bg-gray-100 text-gray-600",
};

export const EventsPanel = ({ getAuthHeader, onRefresh }) => {
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    title: "", description: "", event_type: "class", start_date: "", end_date: "",
    start_time: "", end_time: "", location: "", max_capacity: 0, is_public: true,
  });

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/admin/events`, getAuthHeader());
      setEvents(res.data);
      setLoaded(true);
    } catch { toast.error("Failed to load events"); }
  };

  if (!loaded) fetchEvents();

  const resetForm = () => {
    setForm({ title: "", description: "", event_type: "class", start_date: "", end_date: "", start_time: "", end_time: "", location: "", max_capacity: 0, is_public: true });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/admin/events/${editingId}`, form, getAuthHeader());
        toast.success("Event updated");
      } else {
        await axios.post(`${API}/admin/events`, form, getAuthHeader());
        toast.success("Event created");
      }
      resetForm();
      fetchEvents();
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleEdit = (evt) => {
    setForm({
      title: evt.title, description: evt.description || "", event_type: evt.event_type,
      start_date: evt.start_date, end_date: evt.end_date || "", start_time: evt.start_time || "",
      end_time: evt.end_time || "", location: evt.location || "",
      max_capacity: evt.max_capacity || 0, is_public: evt.is_public !== false,
    });
    setEditingId(evt.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await axios.delete(`${API}/admin/events/${id}`, getAuthHeader());
      toast.success("Deleted");
      fetchEvents();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-500">{events.length} events</h3>
        <Button data-testid="add-event-btn" onClick={() => { resetForm(); setShowForm(!showForm); }} className="bg-warm-700 hover:bg-warm-800 text-white gap-2">
          <Plus className="w-4 h-4" />{showForm ? "Cancel" : "New Event"}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-gray-50 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required className="mt-1" /></div>
          <div><Label className="text-xs">Type</Label>
            <select value={form.event_type} onChange={e => setForm({ ...form, event_type: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm">
              <option value="class">Class</option><option value="workshop">Workshop</option><option value="meeting">Meeting</option><option value="community">Community Event</option><option value="fundraiser">Fundraiser</option><option value="other">Other</option>
            </select>
          </div>
          <div><Label className="text-xs">Start Date *</Label><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} required className="mt-1" /></div>
          <div><Label className="text-xs">End Date</Label><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Start Time</Label><Input type="time" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">End Time</Label><Input type="time" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Location</Label><Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Max Capacity</Label><Input type="number" value={form.max_capacity} onChange={e => setForm({ ...form, max_capacity: parseInt(e.target.value) || 0 })} className="mt-1" /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Description</Label><textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" /></div>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={form.is_public} onChange={e => setForm({ ...form, is_public: e.target.checked })} id="is_public" />
            <Label htmlFor="is_public" className="text-xs">Public event</Label>
          </div>
          <div className="flex justify-end"><Button type="submit" className="bg-warm-700 hover:bg-warm-800 text-white">{editingId ? "Update" : "Create"} Event</Button></div>
        </form>
      )}

      <div className="space-y-3">
        {events.map(evt => (
          <div key={evt.id} className="bg-white border border-gray-100 rounded-xl px-5 py-4 flex items-start justify-between hover:shadow-sm transition-shadow">
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-gray-900">{evt.title}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeColors[evt.event_type]}`}>{evt.event_type}</span>
                {!evt.is_public && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Private</span>}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {evt.start_date}{evt.end_date && evt.end_date !== evt.start_date ? ` - ${evt.end_date}` : ""}
                {evt.start_time ? ` at ${evt.start_time}` : ""}{evt.location ? ` | ${evt.location}` : ""}
              </p>
              {evt.description && <p className="text-sm text-gray-600 mt-1">{evt.description}</p>}
            </div>
            <div className="flex items-center gap-1 ml-3">
              <Button variant="ghost" size="sm" onClick={() => handleEdit(evt)} className="h-8 w-8 p-0"><Edit className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(evt.id)} className="h-8 w-8 p-0 text-red-500"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
        {events.length === 0 && <div className="py-8 text-center text-gray-400 text-sm">No events created yet</div>}
      </div>
    </div>
  );
};
